/**
 * apps/command/src/lib/format.ts — numbers and clocks.
 *
 * TABULAR NUMERALS ARE MANDATORY (§15.3): "numbers must not jitter as they
 * update". Every figure in this panel is inside a `tabular-nums` class, and
 * this module is where the values that go in them are shaped.
 *
 * §15.10's rule that a "no data" state must say WHY there is no data is
 * enforced by types here rather than by discipline: these helpers take
 * `number | null | undefined` and return `null` for absent input, so a caller
 * cannot accidentally render `0` or `NaN%` for a sensor a kiosk does not have.
 * The screen decides what «nomaʼlum» looks like; this file refuses to invent a
 * number.
 */

/** Asia/Tashkent, always (§2). The panel's clock is the government's clock. */
const TIME_ZONE = 'Asia/Tashkent';

const timeFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const dateTimeFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: TIME_ZONE,
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

export function clock(at: Date): string {
  return timeFormatter.format(at);
}

export function stamp(at: Date): string {
  return dateTimeFormatter.format(at);
}

/**
 * A coarse age, as the parts a caller feeds to `t('time.*')`.
 *
 * Returns the largest unit that is at least 1, so a tile reads «4 daqiqa oldin»
 * rather than «247 soniya oldin». Precision above a day is a date.
 *
 * ONE DELIBERATE EXCEPTION: seconds are kept up to 120, not 60.
 *
 * §4.3's healthy bound is 90 seconds, and rounding to minutes there produces a
 * tile that says «1 daqiqa oldin» beside a «Nosoz» pill — two true statements
 * that read as a contradiction, on the one threshold an operator most needs to
 * trust. «95 soniya oldin» beside «Nosoz» explains itself. The second minute is
 * the only place this matters, which is why it is the only place that pays for
 * the extra precision.
 */
export function ageParts(from: Date, now: Date): { key: string; n: number } {
  const seconds = Math.max(0, Math.floor((now.getTime() - from.getTime()) / 1000));
  if (seconds < 10) return { key: 'time.justNow', n: 0 };
  if (seconds < 120) return { key: 'time.secondsAgo', n: seconds };
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return { key: 'time.minutesAgo', n: minutes };
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return { key: 'time.hoursAgo', n: hours };
  return { key: 'time.daysAgo', n: Math.floor(hours / 24) };
}

/**
 * `ageParts` already translated — the form every screen actually wants.
 *
 * `translate` is passed in rather than imported so this module stays free of a
 * dictionary dependency and can be unit-tested with a stub. It is always `t`.
 */
export function ageLabel(
  from: Date,
  now: Date,
  translate: (key: string, params?: Record<string, string | number>) => string,
): string {
  const parts = ageParts(from, now);
  return translate(parts.key, { n: parts.n });
}

/** One decimal, or `null` when there is nothing to report. */
export function percent(value: number | null | undefined): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return value.toFixed(value < 10 ? 1 : 0);
}

export function integer(value: number | null | undefined): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return String(Math.round(value));
}

/** Uptime as whole days/hours/minutes — the largest unit that is at least 1. */
export function durationParts(seconds: number | null | undefined): { key: string; n: number } | null {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 0) return null;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return { key: 'unit.minutes', n: minutes };
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return { key: 'unit.hours', n: hours };
  return { key: 'unit.days', n: Math.floor(hours / 24) };
}

/**
 * A delta against yesterday, as a signed string — or `null` when there is no
 * comparison to make.
 *
 * `+0` is deliberately rendered as `0` with no sign: a plus sign in front of
 * nothing reads as an increase at a glance, and §15.7's figures are read at a
 * glance by definition.
 */
export function delta(today: number, yesterday: number | null | undefined): string | null {
  if (typeof yesterday !== 'number' || !Number.isFinite(yesterday)) return null;
  const difference = today - yesterday;
  if (difference === 0) return '0';
  return difference > 0 ? `+${difference}` : String(difference);
}

/**
 * A number a caller may safely read out of a telemetry payload.
 *
 * Telemetry is `jsonb` and its field list grows every phase (§4.2), so a value
 * that should be a number may be absent, a string from an older build, or
 * something else entirely. Anything that is not a finite number becomes
 * `null` — which the screens render as «nomaʼlum», never as 0.
 */
export function reading(payload: unknown, ...path: string[]): number | null {
  let node: unknown = payload;
  for (const segment of path) {
    if (node === null || typeof node !== 'object') return null;
    node = (node as Record<string, unknown>)[segment];
  }
  return typeof node === 'number' && Number.isFinite(node) ? node : null;
}

/** The same, for a value that must be a string (a version, a device name). */
export function text(payload: unknown, ...path: string[]): string | null {
  let node: unknown = payload;
  for (const segment of path) {
    if (node === null || typeof node !== 'object') return null;
    node = (node as Record<string, unknown>)[segment];
  }
  return typeof node === 'string' && node.length > 0 ? node : null;
}

/** And for a boolean — `null` when the kiosk did not report the field at all. */
export function flag(payload: unknown, ...path: string[]): boolean | null {
  let node: unknown = payload;
  for (const segment of path) {
    if (node === null || typeof node !== 'object') return null;
    node = (node as Record<string, unknown>)[segment];
  }
  return typeof node === 'boolean' ? node : null;
}
